import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"
import { getTgWebApp } from "@/helpers/tgWebApp"
import { useAppSelector } from "@/store"
import { AppSelectors } from "@/store/slices/app"
import {
  type Orientation,
  WebAppViewportContext
} from "./WebAppViewport.context"

const isClient = typeof window !== "undefined"

const getOrientation = (): Orientation => {
  if (!isClient) {
    return "portrait"
  }
  return window.innerWidth >= window.innerHeight ? "landscape" : "portrait"
}

const MOBILE_USER_AGENT =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i

const getViewport = () => {
  if (!isClient) {
    return { width: 0, height: 0 }
  }
  return { width: window.innerWidth, height: window.innerHeight }
}

const checkTelegramAvailable = () => {
  const telegram = getTgWebApp()
  return Boolean(telegram && telegram.version !== "6.0")
}

type ViewportChangedPayload = {
  isStateStable: boolean
  viewportHeight: number
  viewportStableHeight: number
}

export const WebAppViewportProvider = ({
  children
}: PropsWithChildren<object>) => {
  const isDev = useAppSelector(AppSelectors.selectIsDevEnv())

  const [orientation, setOrientation] = useState<Orientation>(getOrientation())
  const [viewport, setViewport] = useState<{ width: number; height: number }>(
    () => getViewport()
  )
  const [isTelegramAvailable, setIsTelegramAvailable] = useState<boolean>(() =>
    checkTelegramAvailable()
  )

  const isMobile = useMemo(() => {
    const telegram = getTgWebApp()

    if (telegram?.platform) {
      const platform = telegram.platform.toLowerCase()
      if (
        platform.includes("android") ||
        platform.includes("ios") ||
        platform.includes("iphone") ||
        platform.includes("ipad")
      ) {
        return true
      }
    }

    if (typeof navigator !== "undefined") {
      if (MOBILE_USER_AGENT.test(navigator.userAgent)) {
        return true
      }
    }

    if (typeof window !== "undefined" && window.matchMedia) {
      const media = window.matchMedia("(pointer: coarse)")
      if (media.matches) {
        return true
      }
    }

    return false
  }, [])

  const lockOrientation = useCallback(
    async (nextOrientation: Orientation) => {
      if (isTelegramAvailable) {
        try {
          getTgWebApp()?.lockOrientation?.()
          return
        } catch (error) {
          console.warn("Failed to lock orientation via Telegram", error)
        }
      }

      if (typeof window !== "undefined") {
        const orientationApi = window.screen?.orientation

        const lock = orientationApi && (orientationApi as any).lock
        if (typeof lock === "function") {
          try {
            await lock.call(
              orientationApi,
              nextOrientation === "landscape" ? "landscape" : "portrait"
            )
          } catch (error) {
            console.warn("Failed to lock orientation via Screen API", error)
          }
        }
      }
    },
    [isTelegramAvailable]
  )

  const unlockOrientation = useCallback(() => {
    if (isTelegramAvailable) {
      try {
        getTgWebApp()?.unlockOrientation?.()
      } catch (error) {
        console.warn("Failed to unlock orientation via Telegram", error)
      }
    }

    if (typeof window !== "undefined") {
      const orientationApi = window.screen?.orientation
      const unlock = orientationApi && (orientationApi as any).unlock
      if (typeof unlock === "function") {
        try {
          unlock.call(orientationApi)
        } catch (error) {
          console.warn("Failed to unlock orientation via Screen API", error)
        }
      }
    }
  }, [isTelegramAvailable])

  const updateViewportFromWindow = useCallback(() => {
    const nextViewport = getViewport()
    const nextOrientation = getOrientation()

    setViewport((prev) =>
      prev.width === nextViewport.width && prev.height === nextViewport.height
        ? prev
        : nextViewport
    )
    setOrientation((prev) =>
      prev === nextOrientation ? prev : nextOrientation
    )
  }, [])

  useEffect(() => {
    const next = checkTelegramAvailable()
    setIsTelegramAvailable((prev) => (prev === next ? prev : next))
  }, [])

  useEffect(() => {
    const telegram = getTgWebApp()

    if (!telegram?.onEvent) {
      return
    }

    const handler = (event?: ViewportChangedPayload) => {
      const height =
        event?.isStateStable && event?.viewportStableHeight
          ? event.viewportStableHeight
          : (event?.viewportHeight ?? window.innerHeight)

      setViewport((prev) => {
        if (prev.width === window.innerWidth && prev.height === height) {
          return prev
        }
        return { width: window.innerWidth, height }
      })
      setOrientation((prev) => {
        const nextOrientation = getOrientation()
        return prev === nextOrientation ? prev : nextOrientation
      })
    }

    telegram.onEvent("viewportChanged", handler)

    return () => {
      telegram.offEvent?.("viewportChanged", handler)
    }
  }, [])

  useEffect(() => {
    updateViewportFromWindow()
    window.addEventListener("resize", updateViewportFromWindow)
    window.addEventListener("orientationchange", updateViewportFromWindow)

    return () => {
      window.removeEventListener("resize", updateViewportFromWindow)
      window.removeEventListener("orientationchange", updateViewportFromWindow)
    }
  }, [updateViewportFromWindow])

  useEffect(() => {
    // In dev we leave orientation unmanaged so desktop debugging is unaffected.
    if (isDev) {
      return
    }

    if (orientation === "landscape") {
      void lockOrientation("landscape")
      return
    }

    unlockOrientation()
  }, [isDev, lockOrientation, orientation, unlockOrientation])

  const contextValue = useMemo(
    () => ({
      width: viewport.width,
      height: viewport.height,
      orientation,
      isMobile,
      isTelegramEnv: isTelegramAvailable
    }),
    [
      orientation,
      isMobile,
      isTelegramAvailable,
      viewport.height,
      viewport.width
    ]
  )

  return (
    <WebAppViewportContext.Provider value={contextValue}>
      {children}
    </WebAppViewportContext.Provider>
  )
}
